import { useEffect, useRef, useState, type Dispatch, type RefObject } from 'react';
import type { ChatRuntime } from '../api/runtime';
import type { ChatAction } from '../model/reducer';
import type { ChatListFilter, ChatMessage, ChatThread } from '../model/types';
import { applyChatSocketEvent } from './applyChatSocketEvent';
import { subscribeChatRealtime } from './chatRealtimeBus';
import { getChatSocketClient } from './getChatSocketClient';

type UseChatSocketEventsParams = {
  enabled: boolean;
  dispatch: Dispatch<ChatAction>;
  currentUserId: number;
  activeChatId: number | null;
  filter: ChatListFilter;
  runtime: ChatRuntime;
  threadsRef: RefObject<ChatThread[]>;
  onPinnedUpdate?: (chatId: number, messages: ChatMessage[] | null) => void;
  onReconnect?: () => void;
};

export function useChatSocketEvents({
  enabled,
  dispatch,
  currentUserId,
  activeChatId,
  filter,
  runtime,
  threadsRef,
  onPinnedUpdate,
  onReconnect,
}: UseChatSocketEventsParams) {
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const paramsRef = useRef({
    dispatch,
    currentUserId,
    activeChatId,
    filter,
    runtime,
    threadsRef,
    onPinnedUpdate,
    onReconnect,
  });

  useEffect(() => {
    paramsRef.current = {
      dispatch,
      currentUserId,
      activeChatId,
      filter,
      runtime,
      threadsRef,
      onPinnedUpdate,
      onReconnect,
    };
  });

  useEffect(() => {
    if (!enabled) {
      setIsSocketConnected(false);
      return;
    }

    let client: ReturnType<typeof getChatSocketClient>;

    try {
      client = getChatSocketClient();
      client.connect();
      setIsSocketConnected(client.isConnected());
    } catch (error) {
      console.error('[ChatSocket] events connect failed', error);
      setIsSocketConnected(false);
      return;
    }

    const offConnect = client.onConnect(() => {
      setIsSocketConnected(true);
    });

    const offDisconnect = client.onDisconnect(() => {
      setIsSocketConnected(false);
    });

    const offBus = subscribeChatRealtime(
      (signal) => {
        const params = paramsRef.current;

        if (signal.kind === 'reconnect') {
          params.onReconnect?.();
          return;
        }

        const result = applyChatSocketEvent(signal.envelope, {
          currentUserId: params.currentUserId,
          activeChatId: params.activeChatId,
          filter: params.filter,
          runtime: params.runtime,
          existingThreads: params.threadsRef.current ?? [],
        });

        result.actions.forEach((action) => {
          params.dispatch(action);
        });

        if (result.sideEffects.pinned) {
          params.onPinnedUpdate?.(
            result.sideEffects.pinned.chatId,
            result.sideEffects.pinned.messages,
          );
        }
      },
      ['all', 'list', 'reconnect'],
    );

    return () => {
      offConnect();
      offDisconnect();
      offBus();
      setIsSocketConnected(false);
    };
  }, [enabled]);

  return { isSocketConnected };
}
